import { motion } from "framer-motion"
import { Button } from "./button"
import { Plus } from "lucide-react"

interface NoDataProps {
  image?: string
  title: string
  description?: string
  actionLabel?: string
  onAction?: () => void
  secondaryLabel?: string
  onSecondary?: () => void
  size?: "sm" | "md" | "lg"
  className?: string
  showActionIcon?: boolean
}

const sizes = {
  sm: { wrap: "py-8",  img: "h-28",  title: "text-sm",   desc: "text-xs"  },
  md: { wrap: "py-14", img: "h-44",  title: "text-base", desc: "text-sm"  },
  lg: { wrap: "py-20", img: "h-60",  title: "text-lg",   desc: "text-sm"  },
}

export function NoData({
  image,
  title,
  description,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  size = "md",
  className = "",
  showActionIcon = true,
}: NoDataProps) {
  const s = sizes[size]

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`flex flex-col items-center justify-center text-center gap-5 w-full ${s.wrap} ${className}`}
    >
      {image && (
        <motion.div
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut", delay: 0.05 }}
          className={`${s.img} flex items-center justify-center select-none`}
        >
          <img
            src={image}
            alt="No data"
            className="h-full w-auto object-contain drop-shadow-sm"
            draggable={false}
          />
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.12 }}
        className="space-y-1.5 max-w-xs"
      >
        <p className={`font-semibold text-foreground ${s.title}`}>{title}</p>
        {description && (
          <p className={`text-muted-foreground leading-relaxed ${s.desc}`}>{description}</p>
        )}
      </motion.div>

      {(actionLabel || secondaryLabel) && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="flex items-center gap-2 flex-wrap justify-center"
        >
          {secondaryLabel && onSecondary && (
            <Button variant="outline" size="sm" onClick={onSecondary}>
              {secondaryLabel}
            </Button>
          )}
          {actionLabel && onAction && (
            <Button size="sm" onClick={onAction} className="gap-1.5">
              {showActionIcon && <Plus className="w-3.5 h-3.5" />}
              {actionLabel}
            </Button>
          )}
        </motion.div>
      )}
    </motion.div>
  )
}
